import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Project, Proposal } from '../../models/types';
import { Link } from 'react-router-dom';
import { Clock, Eye, ChevronRight, ChevronDown, ChevronUp, MessageSquare, Edit, Calendar } from 'lucide-react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import ChatModal from '../../components/Chat/ChatModal';

interface ProposalWithProject extends Proposal {
  project: Project;
  updateCount: number;
  lastRequesterMessageDate?: Date;
  hasRequesterMessage: boolean;
}

const ProviderSubmissions = () => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const [proposals, setProposals] = useState<ProposalWithProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSection, setExpandedSection] = useState<'pending' | 'accepted' | null>(null);
  const [selectedChat, setSelectedChat] = useState<{
    projectId: string;
    providerId: string;
    providerName: string;
  } | null>(null);
  const [stats, setStats] = useState({
    totalSubmissions: 0,
    acceptedSubmissions: 0,
    pendingSubmissions: 0
  });

  useEffect(() => {
    if (!currentUser) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch proposals
        const proposalsRef = collection(db, 'proposals');
        const q = query(
          proposalsRef,
          where('providerId', '==', currentUser.uid)
        );
        
        const querySnapshot = await getDocs(q);
        const proposalList: ProposalWithProject[] = [];
        
        // Fetch associated project data for each proposal
        for (const proposalDoc of querySnapshot.docs) {
          const proposalData = proposalDoc.data();
          const projectRef = doc(db, 'projects', proposalData.projectId);
          const projectSnap = await getDoc(projectRef);
          
          if (projectSnap.exists()) {
            const projectData = projectSnap.data();
            
            // Calculate update count
            const updateCount = proposalData.updates ? proposalData.updates.length : 0;
            
            // Get last requester message date from chat (only for accepted proposals)
            let lastRequesterMessageDate: Date | undefined;
            let hasRequesterMessage = false;
            
            if (proposalData.status === 'accepted') {
              try {
                const chatId = `${proposalData.projectId}_${currentUser.uid}`;
                console.log(`Checking chat messages for chatId: ${chatId}, projectId: ${proposalData.projectId}`);
                
                const messagesRef = collection(db, `projects/${proposalData.projectId}/chats/${chatId}/messages`);
                const messagesQuery = query(
                  messagesRef,
                  where('senderId', '==', projectData.userId)
                );
                
                const messagesSnap = await getDocs(messagesQuery);
                console.log(`Found ${messagesSnap.size} messages from requester for project ${proposalData.projectId}`);
                
                if (!messagesSnap.empty) {
                  hasRequesterMessage = true;
                  // Find the most recent message from requester
                  const requesterMessages = messagesSnap.docs
                    .map(doc => {
                      const data = doc.data();
                      return {
                        ...data,
                        createdAt: data.createdAt?.toDate() || new Date(0)
                      };
                    })
                    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
                  
                  if (requesterMessages.length > 0) {
                    lastRequesterMessageDate = requesterMessages[0].createdAt;
                    console.log(`Last requester message date: ${lastRequesterMessageDate}`);
                  }
                }
              } catch (chatError) {
                console.warn('Could not fetch chat messages for proposal:', proposalDoc.id, chatError);
                // Continue without chat data
              }
            }
            
            proposalList.push({
              id: proposalDoc.id,
              ...proposalData,
              createdAt: proposalData.createdAt.toDate(),
              updateCount,
              lastRequesterMessageDate,
              hasRequesterMessage,
              project: {
                id: projectSnap.id,
                ...projectData,
                createdAt: projectData.createdAt.toDate(),
                updatedAt: projectData.updatedAt.toDate()
              }
            } as ProposalWithProject);
          }
        }
        
        // Sort proposals by date
        proposalList.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        
        setProposals(proposalList);
        
        // Calculate stats
        setStats({
          totalSubmissions: proposalList.length,
          acceptedSubmissions: proposalList.filter(p => p.status === 'accepted').length,
          pendingSubmissions: proposalList.filter(p => p.status === 'pending').length
        });
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(t('error.loading'));
        toast.error(t('error.loading'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, t]);

  const toggleSection = (section: 'pending' | 'accepted') => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const openChat = (projectId: string, providerId: string, providerName: string) => {
    setSelectedChat({ projectId, providerId, providerName });
  };

  const closeChat = () => {
    setSelectedChat(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'accepted':
        return 'bg-green-100 text-green-700';
      case 'rejected':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getTimelineLabel = (timeline: string) => {
    switch (timeline) {
      case '1':
        return t('project.timeline1');
      case '2-3':
        return t('project.timeline2-3');
      case '3-6':
        return t('project.timeline3-6');
      case '6+':
        return t('project.timeline6+');
      default:
        return t('project.timelineUnknown');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">{t('submissions.mySubmissions')}</h1>

      {/* Stats Overview */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div 
          className={`bg-white rounded-lg shadow-md p-4 cursor-pointer transition-colors hover:bg-gray-50 ${
            expandedSection === 'pending' ? 'ring-2 ring-primary-600' : ''
          }`}
          onClick={() => toggleSection('pending')}
        >
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-medium text-gray-500">{t('proposal.pending')}</h3>
            {expandedSection === 'pending' ? (
              <ChevronUp className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            )}
          </div>
          <p className="text-2xl font-bold text-yellow-600">{stats.pendingSubmissions}</p>
        </div>

        <div 
          className={`bg-white rounded-lg shadow-md p-4 cursor-pointer transition-colors hover:bg-gray-50 ${
            expandedSection === 'accepted' ? 'ring-2 ring-primary-600' : ''
          }`}
          onClick={() => toggleSection('accepted')}
        >
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-medium text-gray-500">{t('proposal.accepted')}</h3>
            {expandedSection === 'accepted' ? (
              <ChevronUp className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            )}
          </div>
          <p className="text-2xl font-bold text-green-600">{stats.acceptedSubmissions}</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-sm font-medium text-gray-500">{t('submissions.totalSubmissions')}</h3>
          <p className="text-2xl font-bold text-gray-900">{stats.totalSubmissions}</p>
        </div>
      </div>

      {/* Expanded Sections */}
      {expandedSection && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-lg font-semibold mb-4">
            {expandedSection === 'pending' ? t('submissions.pendingSubmissions') : t('submissions.acceptedSubmissions')}
          </h2>
          
          <div className="space-y-4">
            {proposals
              .filter(proposal => proposal.status === expandedSection)
              .map((proposal) => (
                <div
                  key={proposal.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start mb-3">
                    <Link
                      to={`/project/${proposal.project.id}`}
                      className="text-lg font-medium hover:text-primary-600 transition-colors"
                    >
                      {proposal.project.name}
                    </Link>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(proposal.project.status)}`}>
                        {t(`project.${proposal.project.status}`)}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(proposal.status)}`}>
                        {t(`proposal.${proposal.status}`)}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-3 line-clamp-2">{proposal.project.shortDescription}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-500 mb-4">
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {t('project.timeline')}: {getTimelineLabel(proposal.project.timeline)}
                      </div>
                      <div className="flex items-center">
                        <Eye className="h-4 w-4 mr-1" />
                        {proposal.project.views} {t('project.views')}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Edit className="h-4 w-4 mr-1" />
                        <span className="font-medium">{proposal.updateCount}</span>
                        <span className="ml-1">
                          {proposal.updateCount === 1 ? t('submissions.update') : t('submissions.updates')}
                        </span>
                      </div>
                      
                      {proposal.status === 'accepted' && (
                        <div className="flex items-center">
                          <MessageSquare className="h-4 w-4 mr-1" />
                          {proposal.hasRequesterMessage ? (
                            <button
                              onClick={() => openChat(proposal.project.id, currentUser!.uid, 'Provider')}
                              className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
                            >
                              {t('submissions.youGotMessage')}
                            </button>
                          ) : proposal.lastRequesterMessageDate ? (
                            <span>{t('submissions.lastMessage')}: {format(proposal.lastRequesterMessageDate, 'MMM d, yyyy')}</span>
                          ) : (
                            <span className="text-gray-400">{t('submissions.noMessages')}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>{t('submissions.submittedOn')} {proposal.createdAt.toLocaleDateString()}</span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {proposal.status === 'accepted' && proposal.hasRequesterMessage && (
                        <button
                          onClick={() => openChat(proposal.project.id, currentUser!.uid, 'Provider')}
                          className="px-3 py-1 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 transition-colors flex items-center"
                        >
                          <MessageSquare className="h-3 w-3 mr-1" />
                          {t('chat.chat')}
                        </button>
                      )}
                      
                      <Link
                        to={`/project/${proposal.project.id}`}
                        className="px-4 py-2 text-sm border border-primary-600 text-primary-600 rounded-md hover:bg-primary-50 transition-colors"
                      >
                        {t('project.viewDetails')}
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
              
            {proposals.filter(proposal => proposal.status === expandedSection).length === 0 && (
              <p className="text-center text-gray-500 py-4">
                {t(`submissions.no${expandedSection === 'pending' ? 'Pending' : 'Accepted'}Submissions`)}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Project List when no section is expanded */}
      {!expandedSection && proposals.length === 0 && (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <h2 className="text-xl font-semibold mb-4">{t('submissions.noSubmissions')}</h2>
          <p className="text-gray-600 mb-6">
            {t('submissions.browseMarketplace')}
          </p>
          <Link
            to="/marketplace"
            className="bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors inline-flex items-center"
          >
            {t('marketplace.browseProjects')}
            <ChevronRight className="h-5 w-5 ml-2" />
          </Link>
        </div>
      )}

      {/* Chat Modal */}
      {selectedChat && (
        <ChatModal
          projectId={selectedChat.projectId}
          chatId={`${selectedChat.projectId}_${selectedChat.providerId}`}
          otherUserId={proposals.find(p => p.project.id === selectedChat.projectId)?.project.userId || ''}
          otherUserName="Project Requester"
          onClose={closeChat}
        />
      )}
    </div>
  );
};

export default ProviderSubmissions;